import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, Minus, Trash2, Sun, Moon, Clock, ShoppingCart, ChevronDown, Repeat, Zap, MapPin } from "lucide-react";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useCart } from "../../lib/cart";
import { useT } from "../../lib/i18n";
import { formatINR, todayISO } from "../../lib/utils.fmt";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import AddressDialog from "../../components/AddressDialog";
import LoginRequired from "./LoginRequired";
import EmptyState from "../../components/EmptyState";
import ProductImage from "../../components/ProductImage";
import { earliestDeliveryDate, isWindowOrderable } from "../../lib/cutoff";
import { toast } from "sonner";
import { openRazorpayCheckout, fetchRazorpayOrder } from "../../lib/razorpay";
import { tomorrowIST } from "../../lib/istDate";

const DAYS = [
  { v: 0, k: "Mon" }, { v: 1, k: "Tue" }, { v: 2, k: "Wed" },
  { v: 3, k: "Thu" }, { v: 4, k: "Fri" }, { v: 5, k: "Sat" }, { v: 6, k: "Sun" },
];

// IST calendar date (lib/istDate) — matches the backend's cutoff calendar.
function tomorrowISO() {
  return tomorrowIST();
}

export default function Checkout() {
  const { user, tenant } = useAuth();
  const { t } = useT();
  const nav = useNavigate();
  const { items, setQty, remove, total, count, clear, mrpTotal, sellingTotal, personalDiscount } = useCart();
  const [addresses, setAddresses] = useState([]);
  const [addressId, setAddressId] = useState("");
  const [slot, setSlot] = useState("morning");
  const [deliveryDate, setDeliveryDate] = useState(tomorrowISO());
  // Tracks whether the customer has manually touched the date picker — the
  // sync-to-earliest effect below only auto-updates the default while this
  // is false, so it never silently overrides an explicit choice.
  const [dateTouched, setDateTouched] = useState(false);
  const [instructions, setInstructions] = useState("Leave at door");
  const [addrDialogOpen, setAddrDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // One-time vs. recurring subscription. When 'subscription', each cart line
  // becomes its own Subscription row (start_date = tomorrow), so the customer
  // can pause/edit/cancel each item separately from My Subscriptions.
  const [orderMode, setOrderMode] = useState("one_time");
  // Wallet-debit-at-delivery (default, unchanged behavior) vs pay online now.
  // Online prepay only applies to one-time orders — subscriptions are
  // recurring and keep billing the wallet per delivery, same as today.
  const [paymentMethod, setPaymentMethod] = useState("wallet");
  // Subscribe-mode fields — mirror the per-product subscribe form on
  // ProductDetail so the customer can pick frequency + start date + days
  // straight from the cart without going back into each product.
  const [subFreq, setSubFreq] = useState("daily");
  const [subStartDate, setSubStartDate] = useState(tomorrowISO());
  const [subDays, setSubDays] = useState([]);
  // Trial eligibility — fetched from /api/customer/home to decide whether to
  // show "FREE TRIAL" badge on the Subscribe mode card.
  const [trial, setTrial] = useState({ enabled: false, eligible: false, days: 0 });
  // Cutoff-aware earliest date the customer can pick. Walks day-by-day until
  // at least one active window's cutoff (offset + clock time) hasn't passed.
  // The slot picker reads directly from tenant.delivery_windows so only
  // enabled slots are shown. The backend re-validates on POST.
  const earliestDate = earliestDeliveryDate(tenant?.delivery_windows || []) || tomorrowISO();
  // Slot options sourced from tenant config (Morning / Evening / N-th slot).
  // Falls back to legacy morning+evening if tenant has no windows configured.
  const slotOptions = (tenant?.delivery_windows || [])
    .filter((w) => w.is_active !== false)
    .map((w) => ({ v: (w.label || "").toLowerCase(), l: w.label || "Slot", win: w }));
  const baseSlotOptions = slotOptions.length
    ? slotOptions
    : [{ v: "morning", l: "Morning" }, { v: "evening", l: "Evening" }];
  // Narrow to whatever the selected address's area allows (e.g. an
  // evening-only area) — empty allowed_slots on the address means no
  // restriction, so every tenant-wide slot stays available.
  const selectedAddress = addresses.find((a) => a.id === addressId);
  const addressUnserviceable = selectedAddress?.serviceable === false;
  const areaAllowedSlots = selectedAddress?.allowed_slots || [];
  const effectiveSlotOptions = areaAllowedSlots.length
    ? baseSlotOptions.filter((s) => areaAllowedSlots.includes(s.v))
    : baseSlotOptions;

  const reloadAddresses = async (selectFirstIfNew = false) => {
    const prevIds = new Set(addresses.map((a) => a.id));
    try {
      const { data } = await api.get("/api/customer/addresses");
      setAddresses(data.addresses);
      // If we just saved a NEW address, auto-select it so the customer can
      // place the order without an extra tap.
      const fresh = (data.addresses || []).find((a) => !prevIds.has(a.id));
      if (selectFirstIfNew && fresh) {
        setAddressId(fresh.id);
      } else if (data.addresses.length && !addressId) {
        // Prefer the account-wide default (same as Home/Header) over raw
        // array position — otherwise Checkout could silently pre-select a
        // stale, non-default address while Home shows a different one.
        const def = data.addresses.find((a) => a.is_default);
        setAddressId((def || data.addresses[0]).id);
      }
    } catch (e) {
      toast.error(friendlyMessage(e));
    }
  };

  useEffect(() => {
    if (user) {
      reloadAddresses();
      // Pull trial flags from /api/customer/home (single source of truth).
      api.get("/api/customer/home").then(({ data }) => setTrial(data.trial || { enabled: false, eligible: false, days: 0 })).catch(() => {});
    }
  }, [user]);

  // Keep the delivery date synced to the cutoff-computed earliest date
  // (which reads tenant.delivery_windows and may resolve to TODAY for a
  // same-day-eligible tenant, not just tomorrow) as long as the customer
  // hasn't picked a date themselves — without this, checkout always
  // defaulted to tomorrowISO() regardless of same-day availability, so a
  // same-day-eligible tenant's customers had to notice and manually reopen
  // the date picker to find today was actually orderable.
  // Once the customer HAS picked a date, only correct it forward (never
  // silently override an explicit, still-adjustable choice) — this covers
  // the case where cutoff passes while the page sits open.
  useEffect(() => {
    if (!dateTouched) {
      if (deliveryDate !== earliestDate) setDeliveryDate(earliestDate);
    } else if (deliveryDate < earliestDate) {
      setDeliveryDate(earliestDate);
    }
  }, [earliestDate, deliveryDate, dateTouched]);

  // If the previously-selected slot is no longer available for this tenant,
  // snap to the first valid slot to avoid posting an invalid order.
  useEffect(() => {
    if (effectiveSlotOptions.length && !effectiveSlotOptions.find((s) => s.v === slot)) {
      setSlot(effectiveSlotOptions[0].v);
    }
  }, [effectiveSlotOptions, slot]);

  if (!user) {
    return <LoginRequired title="Sign in to checkout" description="Save your cart and complete the order." />;
  }

  const primary = tenant?.branding?.primary_color || "#3A86FF";

  const placeOrder = async () => {
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    if (!addressId) { toast.error("Please add a delivery address"); return; }
    if (addressUnserviceable) { toast.error("This location isn't serviceable. Please select a different delivery address."); return; }
    setSubmitting(true);
    try {
      if (orderMode === "subscription") {
        if ((subFreq === "weekly" || subFreq === "custom") && subDays.length === 0) {
          toast.error("Pick at least one delivery day");
          setSubmitting(false);
          return;
        }
        // Turn the cart into N subscriptions (one per line).
        const payload = {
          items: items.map((i) => ({ product_id: i.id, variant_id: i.variant_id ?? null, quantity: i.qty })),
          slot,
          start_date: subStartDate,
          address_id: addressId,
          instructions,
          frequency: subFreq,
          custom_days: (subFreq === "weekly" || subFreq === "custom") ? subDays : [],
        };
        const { data } = await api.post("/api/customer/subscriptions/bulk", payload);
        toast.success(`Created ${data.count} subscription${data.count === 1 ? "" : "s"} · starting ${subStartDate}`);
        clear();
        nav("/app/subscriptions");
        return;
      }
      const cartItems = items.map((i) => ({ product_id: i.id, variant_id: i.variant_id ?? null, quantity: i.qty }));

      if (paymentMethod === "online") {
        const params = new URLSearchParams({
          items: JSON.stringify(cartItems), slot, delivery_date: deliveryDate,
          address_id: addressId, instructions,
        });
        const order = await fetchRazorpayOrder(`/api/customer/orders/razorpay-order?${params.toString()}`);
        if (order.mode === "mock") {
          toast.success(`Order placed — ${order.count} item${order.count === 1 ? "" : "s"} · ${formatINR(order.total)}`);
          clear();
          nav("/app/orders");
          return;
        }
        try {
          const res = await openRazorpayCheckout({
            key: order.key_id,
            amount: order.amount_paise,
            currency: order.currency,
            order_id: order.order_id,
            name: tenant?.name || "Order payment",
            description: `${cartItems.length} item${cartItems.length === 1 ? "" : "s"}`,
          });
          const { data } = await api.post("/api/customer/orders/verify-payment", {
            pending_order_id: order.pending_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_signature: res.razorpay_signature,
          });
          toast.success(`Order placed — ${data.count} item${data.count === 1 ? "" : "s"} · ${formatINR(data.total)}`);
          clear();
          nav("/app/orders");
        } catch (e) {
          if (e?.cancelled) { /* user closed checkout — cart stays intact, nothing was written */ }
          else toast.error(e?.description || e?.response?.data?.detail || "Payment failed");
        }
        return;
      }

      const payload = {
        items: cartItems,
        slot, delivery_date: deliveryDate,
        address_id: addressId,
        instructions,
      };
      const { data } = await api.post("/api/customer/orders/cart", payload);
      toast.success(`Order placed — ${data.count} item${data.count === 1 ? "" : "s"} · ${formatINR(data.total)}`);
      clear();
      nav("/app/orders");
    } catch (e) {
      toast.error(e?.description || e.response?.data?.detail || "Failed to place order");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="page-enter pb-52 min-h-screen flex flex-col" data-testid="checkout-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app/products" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight flex-1">Checkout</h1>
          <span className="text-xs font-bold text-neutral-500" data-testid="checkout-count">{count} item{count === 1 ? "" : "s"}</span>
        </div>
      </header>

      <main className="px-5 space-y-5 flex-1 flex flex-col">
        {items.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="Your cart is empty"
            description="Add some products to get started."
            action={
              <Link to="/app/products" className="inline-flex items-center px-5 h-11 rounded-2xl font-bold text-sm text-white"
                    style={{ background: primary }}
                    data-testid="empty-cart-shop">Continue shopping</Link>
            }
            fullHeight
            testId="empty-cart"
          />
        ) : (
          <>
            {/* Items */}
            <section className="bg-white rounded-3xl border border-neutral-200 divide-y divide-neutral-100 overflow-hidden" data-testid="cart-items">
              {items.map((it) => {
                const lineId = it.lineId ?? it.id;
                return (
                <div key={lineId} className="p-4 flex items-center gap-3" data-testid={`cart-item-${lineId}`}>
                  <ProductImage src={it.image_url} name={it.name} primary={primary}
                                className="w-16 h-16 rounded-2xl overflow-hidden shrink-0" badgeClassName="w-9 h-9 text-[10px]"/>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{it.name}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{it.pack_size}</div>
                    <div className="font-mono font-bold text-sm mt-0.5">{formatINR(it.price)}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setQty(lineId, it.qty - 1)}
                            className="w-9 h-9 rounded-full bg-brand-cream border border-neutral-200 inline-flex items-center justify-center"
                            data-testid={`item-minus-${lineId}`}>
                      <Minus className="w-4 h-4"/>
                    </button>
                    <span className="font-mono font-extrabold w-6 text-center" data-testid={`item-qty-${lineId}`}>{it.qty}</span>
                    <button onClick={() => setQty(lineId, it.qty + 1)}
                            className="w-9 h-9 rounded-full text-white inline-flex items-center justify-center"
                            style={{ background: primary }}
                            data-testid={`item-plus-${lineId}`}>
                      <Plus className="w-4 h-4"/>
                    </button>
                  </div>
                  <button onClick={() => remove(lineId)} className="text-brand-clay" data-testid={`item-remove-${lineId}`}>
                    <Trash2 className="w-4 h-4"/>
                  </button>
                </div>
                );
              })}
            </section>

            {/* Order mode: one-time vs subscription */}
            <section className="bg-white rounded-3xl border border-neutral-200 p-3" data-testid="order-mode-toggle">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { v: "one_time",     l: "One-time",   sub: "Deliver once",       Icon: Zap },
                  { v: "subscription", l: "Subscribe",  sub: trial.eligible
                                                          ? (trial.is_free
                                                              ? `First ${trial.days} days FREE`
                                                              : `${trial.days}-day trial`)
                                                          : "Daily, save & skip", Icon: Repeat },
                ].map(({ v, l, sub, Icon }) => (
                  <button key={v} onClick={() => setOrderMode(v)}
                          className={`relative h-auto py-3 px-3 rounded-2xl text-left border-2 transition-all ${orderMode === v ? "text-white border-transparent" : "border-neutral-200 bg-white text-neutral-700"}`}
                          style={orderMode === v ? { background: primary } : {}}
                          data-testid={`mode-${v}`}>
                    {v === "subscription" && trial.eligible && (
                      <span className="absolute -top-2 -right-2 bg-brand-leaf text-white text-[9px] uppercase tracking-wider font-extrabold px-2 py-0.5 rounded-full shadow" data-testid="trial-badge">
                        {trial.is_free ? "Free Trial" : `${trial.days}-Day Trial`}
                      </span>
                    )}
                    <Icon className="w-4 h-4 mb-1"/>
                    <div className="font-extrabold text-sm leading-tight">{l}</div>
                    <div className={`text-[10px] uppercase tracking-wider font-bold mt-0.5 ${orderMode === v ? "text-white/80" : "text-neutral-500"}`}>{sub}</div>
                  </button>
                ))}
              </div>
              {orderMode === "subscription" && (
                <div className="mt-3 rounded-2xl bg-brand-cream p-3 text-[11px] text-neutral-700" data-testid="subscription-info">
                  {trial.eligible && trial.is_free ? (
                    <>
                      <b className="text-brand-leaf">🎉 First {trial.days} days are on us.</b> Your
                      wallet won't be charged during the trial. The subscription auto-cancels
                      after day {trial.days} — re-subscribe to continue with regular billing.
                    </>
                  ) : trial.eligible && !trial.is_free ? (
                    <>
                      <b className="text-brand-ink">{trial.days}-day trial · pay as you go.</b> You'll
                      be charged the regular per-delivery price for {trial.days} days, then the
                      subscription auto-cancels. No long-term commitment — re-subscribe to keep going.
                    </>
                  ) : (
                    <>
                      Each item becomes its own subscription starting <b>{subStartDate}</b> ({subFreq}{(subFreq === "weekly" || subFreq === "custom") && subDays.length ? ` · ${subDays.length} d/wk` : ""}).
                      You can pause, edit or cancel each one separately from <b>My Subscriptions</b>.
                    </>
                  )}
                </div>
              )}
            </section>

            {/* Delivery date */}
            <section className="bg-white rounded-3xl border border-neutral-200 p-5 space-y-4">
              {orderMode === "one_time" && (
              <div>
                <div className="font-semibold mb-2">Delivery date</div>
                <div className="relative">
                  <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none"/>
                  <input type="date" value={deliveryDate} min={earliestDate} onChange={(e) => { setDateTouched(true); setDeliveryDate(e.target.value); }}
                         className="w-full h-12 rounded-2xl border border-neutral-200 pl-11 pr-4 text-base appearance-none bg-white"
                         data-testid="checkout-date"/>
                </div>
                {earliestDate !== todayISO() && (
                  <div className="text-[11px] text-neutral-500 mt-1.5" data-testid="cutoff-hint">
                    Cutoff for today&apos;s slots has passed — earliest delivery date is {earliestDate}.
                  </div>
                )}
              </div>
              )}

              {orderMode === "subscription" && (
                <>
                  <div data-testid="sub-frequency-block">
                    <div className="font-semibold mb-2">Frequency</div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { v: "daily",     l: "Daily" },
                        { v: "alternate", l: "Alternate" },
                        { v: "weekly",    l: "Weekly" },
                        { v: "custom",    l: "Custom" },
                      ].map((o) => {
                        const active = subFreq === o.v;
                        return (
                          <button key={o.v} type="button" onClick={() => setSubFreq(o.v)}
                                  className={`h-12 rounded-2xl font-bold text-sm border-2 transition-colors ${active ? "text-white border-transparent" : "border-neutral-200 bg-white text-neutral-700"}`}
                                  style={active ? { background: primary } : {}}
                                  data-testid={`sub-freq-${o.v}`}>{o.l}</button>
                        );
                      })}
                    </div>
                  </div>

                  {(subFreq === "weekly" || subFreq === "custom") && (
                    <div>
                      <div className="font-semibold mb-2">Days of week</div>
                      <div className="grid grid-cols-7 gap-1.5" data-testid="sub-day-chips">
                        {DAYS.map((d) => {
                          const active = subDays.includes(d.v);
                          return (
                            <button key={d.v} type="button"
                                    onClick={() => setSubDays((cur) => cur.includes(d.v) ? cur.filter((x) => x !== d.v) : [...cur, d.v].sort())}
                                    className={`h-12 rounded-2xl font-bold text-xs transition-colors ${active ? "text-white border-transparent" : "bg-white border-2 border-neutral-200 text-neutral-600"}`}
                                    style={active ? { background: primary } : {}}
                                    data-testid={`sub-day-${d.v}`}>{d.k}</button>
                          );
                        })}
                      </div>
                      <div className="text-xs text-neutral-500 mt-2">
                        {subDays.length === 0 ? "Pick at least one day" : `${subDays.length} day${subDays.length === 1 ? "" : "s"} per week`}
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="font-semibold mb-2">Start date</div>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none"/>
                      <input type="date" value={subStartDate} min={todayISO()} onChange={(e) => setSubStartDate(e.target.value)}
                             className="w-full h-12 rounded-2xl border border-neutral-200 pl-11 pr-4 text-base appearance-none bg-white"
                             data-testid="sub-start-date"/>
                    </div>
                  </div>
                </>
              )}

              <div>
                <div className="font-semibold mb-2">Slot</div>
                {/* Slot count comes from tenant.delivery_windows so a 3-slot
                    tenant lays out as a 3-column grid on mobile too. */}
                <div className={`grid gap-2 ${effectiveSlotOptions.length >= 3 ? "grid-cols-3" : "grid-cols-2"}`} data-testid="checkout-slot-grid">
                  {effectiveSlotOptions.map(({ v, l, win }) => {
                    const Icon = v === "evening" ? Moon : Sun;
                    const active = slot === v;
                    // Disable a slot only in one-time mode if its specific
                    // cutoff has passed for the picked delivery date. Subscribe
                    // mode is forward-looking, so we never block.
                    const disabled = orderMode === "one_time" && !!win && !isWindowOrderable(win, deliveryDate);
                    return (
                      <button key={v} onClick={() => !disabled && setSlot(v)}
                              disabled={disabled}
                              className={`h-auto py-2 rounded-2xl font-bold text-sm border-2 flex flex-col items-center justify-center gap-0.5 ${active ? "text-white border-transparent" : "border-neutral-200 bg-white text-neutral-700"} ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
                              style={active ? { background: primary } : {}}
                              data-testid={`checkout-slot-${v}`}>
                        <span className="inline-flex items-center gap-1.5"><Icon className="w-4 h-4"/> {l}</span>
                        {win?.slot_start && (
                          <span className={`text-[10px] font-mono tabular-nums ${active ? "text-white/85" : "text-neutral-500"}`}>
                            {win.slot_start} – {win.slot_end}
                          </span>
                        )}
                        {disabled && (
                          <span className="text-[9px] uppercase tracking-wider font-extrabold text-brand-clay" data-testid={`checkout-slot-${v}-locked`}>
                            cutoff passed
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="font-semibold mb-2">Delivery address</div>
                {addresses.length === 0 ? (
                  <button onClick={() => setAddrDialogOpen(true)}
                          className="w-full h-12 rounded-2xl border-2 border-dashed border-brand-sky text-brand-sky font-bold text-sm inline-flex items-center justify-center gap-2"
                          data-testid="checkout-add-address-btn">
                    + Add delivery address
                  </button>
                ) : (
                  <div className="relative w-full">
                    {/* Dropdown includes "+ Add new address" as the last
                        option — selecting it pops the same AddressDialog map
                        picker, no navigation needed. */}
                    <select value={addressId}
                            onChange={(e) => {
                              if (e.target.value === "__new__") {
                                setAddrDialogOpen(true);
                              } else {
                                setAddressId(e.target.value);
                              }
                            }}
                            className="w-full h-12 rounded-2xl border border-neutral-200 pl-4 pr-9 text-sm bg-white appearance-none truncate font-medium"
                            data-testid="checkout-address-select">
                      {addresses.map((a) => (
                        <option key={a.id} value={a.id}>{a.label} — {a.line1}{a.city ? `, ${a.city}` : ""}</option>
                      ))}
                      <option value="__new__" data-testid="checkout-address-add-option">＋ Add new address…</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none"/>
                  </div>
                )}
                {addressUnserviceable && (
                  <div className="mt-3 rounded-2xl border-2 border-brand-clay/40 bg-brand-clay/10 px-4 py-3 flex items-start gap-3"
                       data-testid="checkout-unserviceable-banner">
                    <div className="w-9 h-9 rounded-xl bg-brand-clay flex items-center justify-center shrink-0 text-white">
                      <MapPin className="w-4 h-4"/>
                    </div>
                    <div className="text-sm leading-snug">
                      <div className="font-extrabold text-brand-clay">Location Not Serviceable</div>
                      <div className="text-neutral-700 mt-0.5">Sorry, we currently don&rsquo;t deliver to this location. Please select a different delivery address.</div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <div className="font-semibold mb-2">Instructions</div>
                <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)}
                          className="rounded-2xl text-base" rows={2} data-testid="checkout-instructions"/>
              </div>

              {orderMode === "one_time" && tenant?.online_payment_enabled !== false && (
                <div data-testid="checkout-payment-method">
                  <div className="font-semibold mb-2">Payment</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { v: "wallet", l: "Pay from wallet", sub: "Debited at delivery" },
                      { v: "online", l: "Pay online now", sub: "Razorpay — UPI/cards" },
                    ].map(({ v, l, sub }) => (
                      <button key={v} type="button" onClick={() => setPaymentMethod(v)}
                              className={`h-auto py-2.5 px-3 rounded-2xl text-left border-2 transition-all ${paymentMethod === v ? "text-white border-transparent" : "border-neutral-200 bg-white text-neutral-700"}`}
                              style={paymentMethod === v ? { background: primary } : {}}
                              data-testid={`payment-method-${v}`}>
                        <div className="font-bold text-sm leading-tight">{l}</div>
                        <div className={`text-[10px] uppercase tracking-wider font-bold mt-0.5 ${paymentMethod === v ? "text-white/80" : "text-neutral-500"}`}>{sub}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Bill summary — full breakdown: MRP → selling price → personal discount → payable */}
            <section className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid="checkout-bill-summary">
              <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-3">Bill summary</div>
              {mrpTotal > sellingTotal && (
                <div className="flex justify-between text-sm py-1.5">
                  <span className="text-neutral-500">MRP ({count} item{count > 1 ? "s" : ""})</span>
                  <span className="font-mono text-neutral-500 line-through" data-testid="checkout-mrp">{formatINR(mrpTotal)}</span>
                </div>
              )}
              {mrpTotal > sellingTotal && (
                <div className="flex justify-between text-sm py-1.5">
                  <span className="text-neutral-600">Product discount</span>
                  <span className="font-mono text-brand-leaf">− {formatINR(mrpTotal - sellingTotal)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm py-1.5">
                <span className="text-neutral-700">Selling price</span>
                <span className="font-mono font-semibold" data-testid="checkout-selling">{formatINR(sellingTotal)}</span>
              </div>
              {personalDiscount > 0 && (
                <div className="flex justify-between text-sm py-1.5" data-testid="checkout-personal-discount">
                  <span className="text-brand-leaf font-semibold">Personal Discount</span>
                  <span className="font-mono text-brand-leaf font-bold">− {formatINR(personalDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm py-1.5">
                <span className="text-neutral-600">Delivery</span>
                <span className="font-mono font-bold text-brand-leaf">FREE</span>
              </div>
              <div className="border-t border-neutral-200 mt-2 pt-3 flex justify-between items-baseline">
                <span className="font-bold">Final payable</span>
                <span className="font-heading font-extrabold text-lg" data-testid="checkout-total">{formatINR(total)}</span>
              </div>
              {personalDiscount > 0 && (
                <div className="mt-1 text-xs text-brand-leaf font-semibold text-right">
                  You saved {formatINR(personalDiscount)} with your personal discount
                </div>
              )}
            </section>
          </>
        )}
      </main>

      {/* See the matching comment in ProductDetail.jsx: a fixed floating
          CTA bar overlapped this page's own form fields (address,
          instructions) at the initial scroll position on desktop, since
          a `fixed` element covers the same viewport pixels regardless of
          how short the actual page content is. Reverts to a normal
          in-flow block at md: and up. */}
      {items.length > 0 && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 w-full max-w-md bg-white border-t border-neutral-200 p-4 z-40 pb-safe md:static md:translate-x-0 md:max-w-none md:mt-6 md:rounded-3xl md:border">
          <Button onClick={placeOrder} disabled={submitting || !addressId || addressUnserviceable}
                  className="w-full h-14 rounded-2xl font-bold text-base text-white"
                  style={{ background: primary }}
                  data-testid="place-order-btn">
            {submitting ? (orderMode === "subscription" ? "Subscribing…" : "Placing…")
              : orderMode === "subscription"
                ? `Subscribe — ${formatINR(total)}/delivery`
                : paymentMethod === "online"
                  ? `Pay ${formatINR(total)} & place order`
                  : `Place order — ${formatINR(total)}`}
          </Button>
        </div>
      )}

      <AddressDialog open={addrDialogOpen}
                     onOpenChange={(o) => {
                       setAddrDialogOpen(o);
                       // If dialog closes without saving, the select may be
                       // stuck on "__new__" — restore to a real address.
                       if (!o && addressId === "__new__") {
                         setAddressId(addresses[0]?.id || "");
                       }
                     }}
                     onSaved={() => reloadAddresses(true)}/>
    </div>
  );
}
